import { defineStore } from 'pinia'
import { useAsyncRequest } from '@/composables/useAsyncRequest'

export const useCartStore = defineStore('cart', () => {
    const { loading, error, request } = useAsyncRequest()
    const loadCartCacheTtlMs = 10 * 1000
    const lastLoadedAt = ref(0)
    const loadCartInFlight = ref(null)

    const cartData = ref({
        items: {},
        conditions: [],
        subTotal: 0,
        total: 0
    })

    /* ----------------------------------------
     * Computed
     * ------------------------------------- */

    const cartItems = computed(() => Object.values(cartData.value.items))
    const cartConditions = computed(() => cartData.value.conditions || [])
    const cartTotal = computed(() => cartData.value.total)
    const cartSubTotal = computed(() => cartData.value.subTotal)

    /* ----------------------------------------
     * Helpers
     * ------------------------------------- */

    const getSessionId = () => localStorage.getItem('cart_session_id')

    const setCartData = (response) => {
        const itemsMap = {}
        for (const item of response.items ?? []) {
            itemsMap[item.id] = item
        }
        cartData.value = {
            items: itemsMap,
            total: response.total ?? 0,
            subTotal: response.subtotal ?? 0,
            conditions: response.conditions ?? []
        }
    }

    /* ----------------------------------------
     * API
     * ------------------------------------- */

    const storeCart = async (payload) => {
        const response = await request('post', '/api/cart', {
            cart_session_id: getSessionId(),
            ...payload
        })
        if (response.cartSessionId) {
            localStorage.setItem('cart_session_id', response.cartSessionId)
        }

        return response
    }

    const loadCart = async (options = {}) => {
        const { force = false } = options
        const cartSessionId = getSessionId()

        if (!force && loadCartInFlight.value) {
            return loadCartInFlight.value
        }

        if (!force && lastLoadedAt.value > 0 && Date.now() - lastLoadedAt.value <= loadCartCacheTtlMs) {
            return {
                items: Object.values(cartData.value.items),
                total: cartData.value.total,
                subtotal: cartData.value.subTotal,
                conditions: cartData.value.conditions
            }
        }

        loadCartInFlight.value = (async () => {
            const response = await request('get', '/api/cart', {
                cart_session_id: cartSessionId
            })

            setCartData(response)
            lastLoadedAt.value = Date.now()
            return response
        })()

        try {
            return await loadCartInFlight.value
        } finally {
            loadCartInFlight.value = null
        }
    }

    /* ----------------------------------------
     * ADD-ONS (INTENT-SAFE)
     * ------------------------------------- */

    // 🛒 Cart-stage add-ons
    const updateCartAddOn = async (payload) => {
        await request('post', '/api/cart/add-ons', payload)
        await loadCart({ force: true })
    }

    const toggleRiskInsurance = async (enabled) => {
        await request('post', '/api/cart/risk-insurance', {
            enabled
        })

        await loadCart({ force: true })
    }

    const clearCart = () => {
        cartData.value = { items: {}, total: 0 }
        lastLoadedAt.value = 0
    }

    /* ----------------------------------------
     * Cart → UI mapping
     * ------------------------------------- */

    const normalizeRequirementFieldValue = (field, rawValue) => {
        if (field?.type !== 'number') {
            return rawValue ?? ''
        }

        const parsed = Number.parseFloat(String(rawValue ?? '').replace(',', '.'))
        return Number.isFinite(parsed) ? parsed : ''
    }

    const extractRequirementValues = (item, requirements) => {
        const inputKey = requirements?.inputKey
        if (!inputKey) {
            return []
        }

        const payloadEntry = item?.attributes?.requirementsPayload?.[inputKey]

        if (!payloadEntry || typeof payloadEntry !== 'object' || !Array.isArray(payloadEntry.items)) {
            return []
        }

        return payloadEntry.items
            .map((row) => {
                if (!Array.isArray(row)) {
                    return null
                }

                return row.reduce((acc, cell) => {
                    if (!cell?.key) {
                        return acc
                    }

                    const field = requirements?.fields?.find((requirementField) => requirementField.name === cell.key)
                    acc[cell.key] = normalizeRequirementFieldValue(field, cell.value)
                    return acc
                }, {})
            })
            .filter((row) => row && Object.keys(row).length > 0)
    }

    const getAddOnValue = (addOn) => {
        const item = cartData.value.items[addOn.id]
        const requirements = addOn?.requirements

        if (!item) {
            if (addOn?.inputType === 'quantity' && requirements?.enabled && requirements?.inputKey) {
                return {
                    value: 0,
                    [requirements.inputKey]: []
                }
            }

            return addOn?.inputType === 'quantity' ? 0 : null
        }
        switch (addOn?.inputType) {
            case 'quantity':
                if (requirements?.enabled && requirements?.inputKey) {
                    const requirementValues = extractRequirementValues(item, requirements)

                    return {
                        value: item.quantity,
                        [requirements.inputKey]: Array.isArray(requirementValues) ? requirementValues : []
                    }
                }

                return item.quantity

            case 'boolean':
                return true

            case 'select':
                return item.attributes?.selected_option_id ?? null

            default:
                return null
        }
    }

    const hasItem = (id) => {
        return !!cartData.value.items[id]
    }

    return {
        cartData,
        cartItems,
        cartTotal,
        cartConditions,
        cartSubTotal,
        getAddOnValue,
        hasItem,
        storeCart,
        loadCart,
        updateCartAddOn,
        clearCart,
        toggleRiskInsurance,
        loading,
        error
    }
})
